import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Users, Phone, Search, Filter, Plus, Trash2, RefreshCw,
  CheckCircle, Clock, XCircle, Play, ChevronDown, ChevronUp,
  MapPin, Calendar, Building, ArrowRight, Loader2, ListPlus
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  processing: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  error: 'bg-red-100 text-red-800 border-red-300',
};

export default function QueueManagementConsole({ user, logout }) {
  const [availableLeads, setAvailableLeads] = useState([]);
  const [todayQueue, setTodayQueue] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    source: '',
    city: '',
    status: '',
    disposition: '',
    search: ''
  });
  
  const [queueStats, setQueueStats] = useState({});
  const [pagination, setPagination] = useState({ skip: 0, limit: 100, total: 0 });

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-automation/queue/filter-options`);
      setFilterOptions(res.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // Fetch available leads
  const fetchAvailableLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.source) params.append('source', filters.source);
      if (filters.city) params.append('city', filters.city);
      if (filters.status) params.append('status', filters.status);
      if (filters.disposition) params.append('disposition', filters.disposition);
      params.append('limit', pagination.limit);
      params.append('skip', pagination.skip);
      
      const res = await axios.get(`${API_URL}/api/ai-automation/queue/leads/available?${params}`);
      setAvailableLeads(res.data.leads || []);
      setPagination(prev => ({ ...prev, total: res.data.total_available }));
    } catch (error) {
      console.error('Error fetching available leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit]);

  // Fetch today's queue
  const fetchTodayQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-automation/queue/today/full`);
      setTodayQueue(res.data.queue || []);
      setQueueStats(res.data.status_breakdown || {});
    } catch (error) {
      console.error('Error fetching today queue:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
    fetchAvailableLeads();
    fetchTodayQueue();
  }, []);

  useEffect(() => {
    fetchAvailableLeads();
  }, [filters, pagination.skip]);

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  // Select all visible leads
  const selectAllVisible = () => {
    const allIds = availableLeads.filter(l => !l.in_todays_queue).map(l => l.lead_id);
    setSelectedLeads(new Set(allIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedLeads(new Set());
  };

  // Add selected leads to queue
  const addToQueue = async () => {
    if (selectedLeads.size === 0) {
      toast.warning('No leads selected');
      return;
    }
    
    setBuilding(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai-automation/queue/build`, {
        lead_ids: Array.from(selectedLeads),
        priority: 1,
        reason: 'manual_selection'
      });
      
      toast.success(`Added ${res.data.added} leads to queue`);
      setSelectedLeads(new Set());
      fetchAvailableLeads();
      fetchTodayQueue();
    } catch (error) {
      toast.error('Failed to add leads to queue');
    } finally {
      setBuilding(false);
    }
  };

  // Remove lead from queue
  const removeFromQueue = async (leadId) => {
    try {
      await axios.delete(`${API_URL}/api/ai-automation/queue/item/${leadId}`);
      toast.success('Removed from queue');
      fetchTodayQueue();
    } catch (error) {
      toast.error('Failed to remove from queue');
    }
  };

  // Clear completed
  const clearCompleted = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/ai-automation/queue/clear-completed`);
      toast.success(`Cleared ${res.data.items_removed} completed items`);
      fetchTodayQueue();
    } catch (error) {
      toast.error('Failed to clear completed');
    }
  };

  // Generate automatic queue
  const generateAutoQueue = async () => {
    setBuilding(true);
    try {
      await axios.post(`${API_URL}/api/ai-automation/queue/generate`);
      toast.success('Queue generated successfully');
      fetchTodayQueue();
    } catch (error) {
      toast.error('Failed to generate queue');
    } finally {
      setBuilding(false);
    }
  };

  // Filter leads by search
  const filteredLeads = availableLeads.filter(lead => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      (lead.name || '').toLowerCase().includes(search) ||
      (lead.phone || '').includes(search) ||
      (lead.city || '').toLowerCase().includes(search)
    );
  });

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Queue Management Console</h1>
            <p className="text-slate-500">Select leads to build the daily calling queue</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchAvailableLeads(); fetchTodayQueue(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={generateAutoQueue} disabled={building}>
              {building ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Auto Generate Queue
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-900">{pagination.total.toLocaleString()}</div>
              <div className="text-sm text-slate-500">Available Leads</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-800">{queueStats.pending || 0}</div>
              <div className="text-sm text-yellow-600">Pending in Queue</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-800">{queueStats.processing || 0}</div>
              <div className="text-sm text-blue-600">Processing</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-800">{queueStats.completed || 0}</div>
              <div className="text-sm text-green-600">Completed Today</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-800">{selectedLeads.size}</div>
              <div className="text-sm text-purple-600">Selected</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="build" className="space-y-4">
          <TabsList>
            <TabsTrigger value="build" className="flex items-center gap-2">
              <ListPlus className="w-4 h-4" /> Build Queue
            </TabsTrigger>
            <TabsTrigger value="today" className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> Today's Queue ({todayQueue.length})
            </TabsTrigger>
          </TabsList>

          {/* Build Queue Tab */}
          <TabsContent value="build" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" /> Filter Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Search</label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Name, phone, city..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Source</label>
                    <select
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      value={filters.source}
                      onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                    >
                      <option value="">All Sources</option>
                      {filterOptions.sources?.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">City</label>
                    <select
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      value={filters.city}
                      onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                    >
                      <option value="">All Cities</option>
                      {filterOptions.cities?.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Status</label>
                    <select
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="">All Statuses</option>
                      {filterOptions.statuses?.map(s => (
                        <option key={s} value={s}>{s} ({filterOptions.status_counts?.[s] || 0})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Disposition</label>
                    <select
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      value={filters.disposition}
                      onChange={(e) => setFilters(prev => ({ ...prev, disposition: e.target.value }))}
                    >
                      <option value="">All Dispositions</option>
                      {filterOptions.dispositions?.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selection Actions */}
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAllVisible}>
                  Select All ({filteredLeads.filter(l => !l.in_todays_queue).length})
                </Button>
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  Clear Selection
                </Button>
              </div>
              <Button 
                onClick={addToQueue} 
                disabled={selectedLeads.size === 0 || building}
                className="bg-green-600 hover:bg-green-700"
              >
                {building ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add {selectedLeads.size} to Queue
              </Button>
            </div>

            {/* Leads Table */}
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-3 text-left w-12">
                          <Checkbox 
                            checked={selectedLeads.size === filteredLeads.filter(l => !l.in_todays_queue).length && selectedLeads.size > 0}
                            onCheckedChange={(checked) => checked ? selectAllVisible() : clearSelection()}
                          />
                        </th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Name</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Phone</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">City</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Source</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Status</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">AI Attempts</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Last Call</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                          </td>
                        </tr>
                      ) : filteredLeads.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-500">
                            No leads found matching filters
                          </td>
                        </tr>
                      ) : (
                        filteredLeads.map(lead => (
                          <tr 
                            key={lead.lead_id} 
                            className={`hover:bg-slate-50 ${lead.in_todays_queue ? 'opacity-50' : ''} ${selectedLeads.has(lead.lead_id) ? 'bg-green-50' : ''}`}
                          >
                            <td className="p-3">
                              <Checkbox 
                                checked={selectedLeads.has(lead.lead_id)}
                                onCheckedChange={() => toggleLeadSelection(lead.lead_id)}
                                disabled={lead.in_todays_queue}
                              />
                            </td>
                            <td className="p-3">
                              <div className="font-medium text-slate-900">{lead.name || 'N/A'}</div>
                              {lead.in_todays_queue && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">In Queue</Badge>
                              )}
                            </td>
                            <td className="p-3 font-mono text-sm">{lead.phone || 'N/A'}</td>
                            <td className="p-3 text-sm">{lead.city || '-'}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-xs">{lead.source || 'Unknown'}</Badge>
                            </td>
                            <td className="p-3">
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  lead.ai_status === 'interested' ? 'bg-green-100 text-green-800' :
                                  lead.ai_status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                  'bg-slate-100 text-slate-800'
                                }`}
                              >
                                {lead.ai_disposition || lead.disposition || lead.status || 'New'}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm text-center">{lead.ai_total_attempts || 0}</td>
                            <td className="p-3 text-sm text-slate-500">
                              {lead.ai_last_call ? new Date(lead.ai_last_call).toLocaleDateString() : 'Never'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
                
                {/* Pagination */}
                <div className="flex justify-between items-center p-4 border-t bg-slate-50">
                  <div className="text-sm text-slate-500">
                    Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={pagination.skip === 0}
                      onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={pagination.skip + pagination.limit >= pagination.total}
                      onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Today's Queue Tab */}
          <TabsContent value="today" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-4">
                <Badge variant="outline" className="px-3 py-1">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending: {queueStats.pending || 0}
                </Badge>
                <Badge variant="outline" className="px-3 py-1 bg-blue-50 text-blue-700">
                  Processing: {queueStats.processing || 0}
                </Badge>
                <Badge variant="outline" className="px-3 py-1 bg-green-50 text-green-700">
                  Completed: {queueStats.completed || 0}
                </Badge>
              </div>
              <Button 
                variant="outline" 
                onClick={clearCompleted}
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Completed
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Lead</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Phone</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">City</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Source</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Priority</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Status</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Added</th>
                        <th className="p-3 text-left text-sm font-medium text-slate-700">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {todayQueue.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-slate-500">
                            No leads in today's queue
                          </td>
                        </tr>
                      ) : (
                        todayQueue.map(item => (
                          <tr key={item.lead_id} className="hover:bg-slate-50">
                            <td className="p-3">
                              <div className="font-medium text-slate-900">{item.name || 'N/A'}</div>
                              <div className="text-xs text-slate-500">{item.lead_id}</div>
                            </td>
                            <td className="p-3 font-mono text-sm">{item.phone || 'N/A'}</td>
                            <td className="p-3 text-sm">{item.city || '-'}</td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-xs">{item.source || 'Unknown'}</Badge>
                            </td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className={item.priority === 1 ? 'bg-red-100 text-red-800' : ''}>
                                {item.priority || 5}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <Badge className={STATUS_COLORS[item.status] || 'bg-slate-100'}>
                                {item.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm text-slate-500">
                              {item.added_at ? new Date(item.added_at).toLocaleTimeString() : '-'}
                            </td>
                            <td className="p-3">
                              {item.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeFromQueue(item.lead_id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
